/**
 * DailyLivePage — /analytics/template/daily-live/:eventId
 *
 * Live-event daily mode with 30s /stats polling. Hero KPI row + hourly
 * trend line (last 4 hours) + per-ride scan density + capacity-fill
 * score + kiosk count.
 *
 * V2.5-B Fork (2026-04-20). Critique HIGH #1: chart relabeled from
 * "15-min rolling" (impossible — stats.hourly is hour-granularity) to
 * "Hourly orders trend (last 4 hr)".
 * Critique MEDIUM #2: queue-depth + active-staff panes replaced with
 * honest kiosk-count + capacity-fill.
 *
 * Feed status mapping matches AnalyticsPage's exactly (consistency is
 * load-bearing — screen-reader users should get the same utterance
 * across both surfaces).
 */

import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import type { BarChart as BarChartPayload, LineChart as LineChartPayload } from '../../types/chart'
import { ChatChart } from '../../components/chat/ChatChart'
import {
  BannerNote,
  DeferredPane,
  TemplateAnalyticsShell,
} from '../../components/analytics/TemplateAnalyticsShell'
import { HeroKpiProgressBar } from '../../components/analytics/HeroKpiProgressBar'
import { CompactScoreTile } from '../../components/analytics/CompactScoreTile'
import { LivePulse, type FeedStatus } from '../../components/analytics/LivePulse'
import type { DashboardStats } from '../../components/analytics/useEventAnalytics'
import { useEventAnalytics } from '../../components/analytics/useEventAnalytics'

const fmtUSD = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)

const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(n)

function parseEventId(raw: string | undefined): number | null {
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null
}

function deriveCapacity(
  metric: 'riders_per_hr' | 'orders_per_hr',
  stats: DashboardStats | null,
): number | null {
  if (!stats) return null
  const hourly = stats.hourly
  if (!hourly || hourly.length === 0) return null
  const key = metric === 'riders_per_hr' ? 'riders' : 'orders'
  const peak = Math.max(...hourly.map((h) => (h[key as 'riders' | 'orders'] as number) || 0))
  if (!Number.isFinite(peak) || peak <= 0) return null
  return Math.round(peak * 1.1)
}

function mapBackendStatus(raw: string | null | undefined): FeedStatus {
  if (!raw) return 'idle'
  const v = String(raw).toLowerCase()
  if (v === 'live') return 'live'
  if (v === 'upcoming') return 'upcoming'
  if (v === 'closed' || v === 'inactive') return 'closed'
  return 'idle'
}

export function DailyLivePage() {
  const { setCurrentEventId } = useAuth()
  const params = useParams<{ eventId?: string }>()
  const eventId = parseEventId(params.eventId)

  useEffect(() => {
    if (eventId) setCurrentEventId(eventId)
  }, [eventId, setCurrentEventId])

  const {
    stats,
    statsLoading,
    statsError,
    extras,
    extrasAvailable,
    yoy,
  } = useEventAnalytics({
    eventId,
    pollStatsEveryMs: 30_000,
  })

  const feedStatus = useMemo<FeedStatus>(() => {
    if (eventId == null) return 'idle'
    if (statsError && stats) return 'paused'
    if (statsError) return 'offline'
    if (!stats) return 'connecting'
    return mapBackendStatus(stats.event.status)
  }, [eventId, stats, statsError])

  const pulseCaptionOverride = useMemo<string | undefined>(() => {
    if (feedStatus === 'paused') return 'Feed paused · auto-retrying every 30 s'
    if (feedStatus === 'upcoming' && stats) return `Upcoming · ${stats.event.event_date}`
    return undefined
  }, [feedStatus, stats])

  const hourlyTrendChart = useMemo<LineChartPayload | null>(() => {
    const hourly = stats?.hourly
    if (!hourly || hourly.length < 2) return null
    // Last 4 hours by hour-index (monotonically increasing). We don't
    // have absolute timestamps per bucket so "last 4 hr" means the
    // trailing 4 rows in the array.
    const tail = hourly.slice(-4)
    if (tail.length < 2) return null
    const allZero = tail.every((h) => !h.orders)
    if (allZero) return null
    return {
      type: 'line',
      title: 'Hourly orders trend (last 4 hr)',
      xLabel: 'Hour',
      yLabel: 'Orders',
      series: [
        {
          label: 'Orders',
          data: tail.map((h) => ({ x: h.hour, y: h.orders || 0 })),
        },
      ],
    }
  }, [stats])

  const perRideDensityChart = useMemo<BarChartPayload | null>(() => {
    const top = extras?.top_attractions?.slice(0, 8) ?? []
    if (top.length === 0) return null
    return {
      type: 'bar',
      title: 'Per-ride scan density',
      orientation: 'horizontal',
      series: top.map((a) => ({ label: a.attraction_name, value: a.scans })),
      xLabel: 'Scans',
    }
  }, [extras])

  const capacityFillPct = useMemo(() => {
    const cap = deriveCapacity('orders_per_hr', stats)
    if (!cap || !stats) return null
    const ratio = Math.max(0, (stats.metrics.orders_per_hour || 0) / cap)
    return Math.round(Math.min(100, ratio * 100))
  }, [stats])

  const kioskCount = useMemo(() => {
    if (!extrasAvailable) return null
    return extras?.kiosk_throughput?.length ?? 0
  }, [extras, extrasAvailable])

  if (eventId == null) {
    return (
      <TemplateAnalyticsShell title="Daily live">
        <div
          data-testid="daily-live-empty"
          className="rounded-card border border-dashed border-hairline bg-surface-stripe p-8 text-center text-sm text-ink-soft dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
        >
          Pick an event to stream its live KPIs, ride density, and capacity.
        </div>
      </TemplateAnalyticsShell>
    )
  }

  return (
    <TemplateAnalyticsShell
      title="Daily live"
      subtitle={stats ? `${stats.event.event_name}` : 'Connecting to feed…'}
      eventName={stats?.event.event_name ?? null}
      windowChip={stats ? stats.event.event_date : null}
      actions={
        <LivePulse
          status={feedStatus}
          lastScanAt={stats?.generated_at ?? null}
          caption={pulseCaptionOverride}
          data-testid="daily-live-pulse"
        />
      }
      banner={
        statsError && !stats ? (
          <BannerNote tone="warning" data-testid="daily-live-error-banner">
            Couldn&apos;t reach the live feed just yet. Auto-retrying every 30
            seconds.
          </BannerNote>
        ) : null
      }
    >
      {/* Hero KPI row */}
      <section
        aria-label="Live hero KPIs"
        className="grid grid-cols-1 gap-4 md:grid-cols-3"
        data-testid="daily-live-hero-row"
      >
        <HeroKpiProgressBar
          metric="revenue"
          value={stats?.metrics.revenue_total ?? null}
          capacity={null}
          yoyPct={yoy?.revenue_total ?? null}
          formatValue={fmtUSD}
          loading={statsLoading && !stats}
          data-testid="daily-live-hero-revenue"
        />
        <HeroKpiProgressBar
          metric="riders_per_hr"
          value={stats?.metrics.riders_per_hour ?? null}
          capacity={deriveCapacity('riders_per_hr', stats)}
          yoyPct={yoy?.riders_per_hour ?? null}
          formatValue={fmtNum}
          loading={statsLoading && !stats}
          data-testid="daily-live-hero-riders"
        />
        <HeroKpiProgressBar
          metric="orders_per_hr"
          value={stats?.metrics.orders_per_hour ?? null}
          capacity={deriveCapacity('orders_per_hr', stats)}
          yoyPct={yoy?.orders_per_hour ?? null}
          formatValue={fmtNum}
          loading={statsLoading && !stats}
          data-testid="daily-live-hero-orders"
        />
      </section>

      {/* Chart + scores row */}
      <section
        aria-label="Live trend + density"
        className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3"
        data-testid="daily-live-chart-row"
      >
        <div className="lg:col-span-2">
          {hourlyTrendChart ? (
            <ChatChart chart={hourlyTrendChart} density="canvas" />
          ) : (
            <DeferredPane
              title="Hourly orders trend (last 4 hr)"
              message="Live data will stream in once the first orders land."
            />
          )}
        </div>
        <div className="flex flex-col gap-3">
          <CompactScoreTile
            label="Capacity fill"
            scorePct={capacityFillPct}
            caption={
              capacityFillPct === null
                ? undefined
                : `${capacityFillPct}% of today's peak orders/hr`
            }
            unavailableMessage="Need an hourly peak to score"
            data-testid="daily-live-capacity"
          />
          <CompactScoreTile
            label="Kiosk count"
            scorePct={
              kioskCount === null
                ? null
                : Math.min(100, Math.round((kioskCount / 8) * 100))
            }
            caption={
              kioskCount === null
                ? undefined
                : `${kioskCount} active kiosk group${kioskCount === 1 ? '' : 's'}`
            }
            unavailableMessage="Kiosk data unavailable"
            data-testid="daily-live-kiosk-count"
          />
        </div>
      </section>

      {/* Per-ride density */}
      <section className="mt-4">
        {perRideDensityChart ? (
          <ChatChart chart={perRideDensityChart} density="canvas" />
        ) : (
          <DeferredPane
            title="Per-ride scan density"
            message={
              extrasAvailable
                ? 'No scans logged yet this event.'
                : 'Attraction rollup isn\u2019t available for this event.'
            }
          />
        )}
      </section>
    </TemplateAnalyticsShell>
  )
}
